// Tax rates tab: the workspace rate table with create/edit/remove, split
// rates (components), and the country-profile starting point (apply + seed).
//
// Rates render as a percent plus basis points. Removal of a rate or a split
// part is confirmed through the shared Dialog. Active/inactive is shown
// explicitly from `is_active`; the typed update call supports setting it, but
// no toggle is surfaced here so the tab's API calls stay exactly as before.
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { StatusDot } from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import {
  type FormEvent,
  Fragment,
  useCallback,
  useEffect,
  useState,
} from "react";
import { AccountPicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import {
  applyTaxProfile,
  createTaxComponent,
  createTaxRate,
  deleteTaxComponent,
  deleteTaxRate,
  ensureTaxSeeded,
  listTaxComponents,
  listTaxRates,
  TAX_RATE_KINDS,
  TAX_RATE_TYPES,
  type TaxComponent,
  type TaxRate,
  updateTaxRate,
} from "@/lib/api";
import { humanizeLabel } from "@/lib/labels";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function rateWithBp(rate_bp: number): string {
  return `${(rate_bp / 100).toFixed(2)}% (${rate_bp} bp)`;
}

function toRateBp(percent: string): number | null {
  const trimmed = percent.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0 || value > 100) return null;
  return Math.round(value * 100);
}

interface SplitPartsPanelProps {
  rate: TaxRate;
  parts: TaxComponent[];
  idPrefix: string;
  compName: string;
  compPercent: string;
  compAccount: string;
  compWorking: boolean;
  onNameChange: (value: string) => void;
  onPercentChange: (value: string) => void;
  onAccountChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onRemove: (part: TaxComponent) => void;
}

function SplitPartsPanel({
  rate,
  parts,
  idPrefix,
  compName,
  compPercent,
  compAccount,
  compWorking,
  onNameChange,
  onPercentChange,
  onAccountChange,
  onSubmit,
  onRemove,
}: SplitPartsPanelProps) {
  return (
    <div className="grid gap-2 py-1">
      {parts.length === 0 ? (
        <p className="text-xs text-[var(--muted-foreground)]">
          No split parts — this rate posts as a single line. Add parts below to
          split it (e.g. state + county).
        </p>
      ) : (
        <ul className="grid gap-1 text-sm">
          {parts.map((part) => (
            <li key={part.id} className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{part.name}</span>
              <span className="tabular-nums">{rateWithBp(part.rate_bp)}</span>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onRemove(part)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-2">
        <label
          className="grid gap-1 text-xs font-medium"
          htmlFor={`${idPrefix}-name-${rate.id}`}
        >
          Part name
          <Input
            id={`${idPrefix}-name-${rate.id}`}
            value={compName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="State part"
            disabled={compWorking}
          />
        </label>
        <label
          className="grid gap-1 text-xs font-medium"
          htmlFor={`${idPrefix}-rate-${rate.id}`}
        >
          Rate (%)
          <Input
            id={`${idPrefix}-rate-${rate.id}`}
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={compPercent}
            onChange={(e) => onPercentChange(e.target.value)}
            disabled={compWorking}
          />
        </label>
        <label
          className="grid min-w-44 gap-1 text-xs font-medium"
          htmlFor={`${idPrefix}-account-${rate.id}`}
        >
          Ledger account (optional)
          <AccountPicker
            id={`${idPrefix}-account-${rate.id}`}
            value={compAccount}
            onChange={onAccountChange}
            disabled={compWorking}
          />
        </label>
        <Button type="submit" variant="secondary" disabled={compWorking}>
          {compWorking ? "Adding…" : "Add part"}
        </Button>
      </form>
    </div>
  );
}

export function RatesTab() {
  const [rates, setRates] = useState<TaxRate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [percent, setPercent] = useState("");
  const [rateType, setRateType] = useState("sales");
  const [kind, setKind] = useState("standard");
  const [recoverable, setRecoverable] = useState("100");
  const [isDefault, setIsDefault] = useState(false);

  const [editing, setEditing] = useState<TaxRate | null>(null);
  const [editPercent, setEditPercent] = useState("");
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [components, setComponents] = useState<Record<string, TaxComponent[]>>(
    {},
  );
  const [compName, setCompName] = useState("");
  const [compPercent, setCompPercent] = useState("");
  const [compAccount, setCompAccount] = useState("");
  const [compWorking, setCompWorking] = useState(false);

  const [country, setCountry] = useState("");
  const [profiling, setProfiling] = useState(false);

  const [confirmRate, setConfirmRate] = useState<TaxRate | null>(null);
  const [confirmComponent, setConfirmComponent] = useState<{
    rate: TaxRate;
    component: TaxComponent;
  } | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    listTaxRates()
      .then(setRates)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(reload, [reload]);

  const submitCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const rate_bp = toRateBp(percent);
    const recoverable_percent_bp = toRateBp(recoverable);
    if (!name.trim()) {
      setError("Give the rate a name first.");
      return;
    }
    if (rate_bp === null) {
      setError("Rate must be a percent from 0 to 100.");
      return;
    }
    if (recoverable_percent_bp === null) {
      setError("Recoverable part must be a percent from 0 to 100.");
      return;
    }
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      await createTaxRate({
        name: name.trim(),
        rate_bp,
        rate_type: rateType,
        kind,
        recoverable_percent_bp,
        is_default: isDefault,
      });
      setNotice(`Added “${name.trim()}” at ${rateWithBp(rate_bp)}.`);
      setName("");
      setPercent("");
      setIsDefault(false);
      setCreateOpen(false);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (rate: TaxRate) => {
    setEditing(rate);
    setEditName(rate.name);
    setEditPercent(String(rate.rate_bp / 100));
  };

  const submitEdit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing) return;
    const rate_bp = toRateBp(editPercent);
    if (!editName.trim()) {
      setError("Give the rate a name first.");
      return;
    }
    if (rate_bp === null) {
      setError("Rate must be a percent from 0 to 100.");
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await updateTaxRate(editing.id, { name: editName.trim(), rate_bp });
      setNotice(`Updated “${editName.trim()}”.`);
      setEditing(null);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const removeRate = async (rate: TaxRate) => {
    setError(null);
    setNotice(null);
    try {
      await deleteTaxRate(rate.id);
      setNotice(`Removed “${rate.name}”.`);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    }
  };

  const toggleComponents = async (rate: TaxRate) => {
    if (expanded === rate.id) {
      setExpanded(null);
      return;
    }
    setExpanded(rate.id);
    setError(null);
    try {
      const rows = await listTaxComponents(rate.id);
      setComponents((prev) => ({ ...prev, [rate.id]: rows }));
    } catch (cause) {
      setError(toErrorMessage(cause));
    }
  };

  const submitComponent = async (
    event: FormEvent<HTMLFormElement>,
    rate: TaxRate,
  ) => {
    event.preventDefault();
    const rate_bp = toRateBp(compPercent);
    if (!compName.trim()) {
      setError("Give the split part a name first.");
      return;
    }
    if (rate_bp === null) {
      setError("Split rate must be a percent from 0 to 100.");
      return;
    }
    setCompWorking(true);
    setError(null);
    setNotice(null);
    try {
      await createTaxComponent(rate.id, {
        name: compName.trim(),
        rate_bp,
        ...(compAccount ? { account_id: compAccount } : {}),
      });
      setNotice(`Added “${compName.trim()}” to “${rate.name}”.`);
      setCompName("");
      setCompPercent("");
      setCompAccount("");
      const rows = await listTaxComponents(rate.id);
      setComponents((prev) => ({ ...prev, [rate.id]: rows }));
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setCompWorking(false);
    }
  };

  const removeComponent = async (rate: TaxRate, component: TaxComponent) => {
    setError(null);
    setNotice(null);
    try {
      await deleteTaxComponent(rate.id, component.id);
      setNotice(`Removed “${component.name}”.`);
      const rows = await listTaxComponents(rate.id);
      setComponents((prev) => ({ ...prev, [rate.id]: rows }));
    } catch (cause) {
      setError(toErrorMessage(cause));
    }
  };

  const runProfile = async (action: "apply" | "seed") => {
    const code = country.trim().toUpperCase();
    if (!code) {
      setError("Enter a two-letter country code first (e.g. US, GB, AU).");
      return;
    }
    setProfiling(true);
    setError(null);
    setNotice(null);
    try {
      if (action === "apply") {
        const result = await applyTaxProfile({ country: code });
        const created = result?.created?.length ?? 0;
        const skipped = result?.skipped?.length ?? 0;
        setNotice(
          `Applied the ${result?.tax_name ?? code} profile: ${created} rate${created === 1 ? "" : "s"} created, ${skipped} skipped.`,
        );
      } else {
        const result = await ensureTaxSeeded({ country: code });
        if (result && "created" in result) {
          const created = result.created?.length ?? 0;
          setNotice(
            `Seeded ${created} rate${created === 1 ? "" : "s"} from the ${code} profile.`,
          );
        } else {
          setNotice(
            result?.seeded
              ? `Rates for ${code} are already in place — nothing to seed.`
              : `Nothing to seed for ${code}.`,
          );
        }
      }
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setProfiling(false);
    }
  };

  return (
    <div className="grid gap-4">
      {error && !createOpen ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-3">
            <Button type="button" variant="secondary" onClick={reload}>
              Retry
            </Button>
          </div>
        </div>
      ) : null}
      {notice ? (
        <output className="surface block rounded-xl border border-[var(--status-success-foreground)] p-4 text-sm">
          {notice}
        </output>
      ) : null}

      <div className="actions-row">
        <Button type="button" onClick={() => setCreateOpen(true)}>New tax rate</Button>
      </div>
      <Drawer
        open={createOpen}
        onOpenChange={(next) => { if (!creating) setCreateOpen(next); }}
        title="New tax rate"
        description="Enter the rate and its reclaimable share. Saving adds it to the rate list; it does not file a return."
        footer={<>
          <Button type="button" variant="secondary" disabled={creating} onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button type="submit" form="tax-rate-create-form" disabled={creating}>{creating ? "Adding…" : "Add rate"}</Button>
        </>}
      >
        {error ? <p role="alert" className="mb-3 text-sm text-[var(--danger)]">{error}</p> : null}
      <form id="tax-rate-create-form" onSubmit={submitCreate} className="grid gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label htmlFor="tr-name" className="grid gap-1 text-xs font-medium">
            Name
            <Input
              id="tr-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Standard VAT"
              disabled={creating}
            />
          </label>
          <label htmlFor="tr-rate" className="grid gap-1 text-xs font-medium">
            Rate (%)
            <Input
              id="tr-rate"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              placeholder="20"
              disabled={creating}
            />
          </label>
          <label htmlFor="tr-type" className="grid gap-1 text-xs font-medium">
            Applies to
            <select
              id="tr-type"
              className="select"
              value={rateType}
              onChange={(e) => setRateType(e.target.value)}
              disabled={creating}
            >
              {TAX_RATE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {humanizeLabel(type)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="tr-kind" className="grid gap-1 text-xs font-medium">
            Kind
            <select
              id="tr-kind"
              className="select"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              disabled={creating}
            >
              {TAX_RATE_KINDS.map((value) => (
                <option key={value} value={value}>
                  {humanizeLabel(value)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="tr-recover" className="grid gap-1 text-xs font-medium">
            Reclaimable (%)
            <Input
              id="tr-recover"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={recoverable}
              onChange={(e) => setRecoverable(e.target.value)}
              disabled={creating}
            />
          </label>
          <label
            htmlFor="tr-default"
            className="flex items-center gap-2 text-xs font-medium"
          >
            <input
              id="tr-default"
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              disabled={creating}
            />
            Default rate
          </label>
        </div>
        <p className="text-xs text-[var(--muted-foreground)]">
          The reclaimable share is the part of this tax the business can
          recover on purchases, entered as a percent.
        </p>
      </form>
      </Drawer>

      <div className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5">
        <label htmlFor="tr-country" className="grid gap-1 text-xs font-medium">
          Start from a country profile
          <Input
            id="tr-country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder="GB"
            maxLength={2}
            disabled={profiling}
          />
        </label>
        <Button
          type="button"
          variant="secondary"
          disabled={profiling}
          onClick={() => void runProfile("apply")}
        >
          {profiling ? "Working…" : "Apply profile"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={profiling}
          onClick={() => void runProfile("seed")}
        >
          {profiling ? "Working…" : "Fill gaps only"}
        </Button>
        <p className="w-full text-xs text-[var(--muted-foreground)]">
          Applying a profile adds that country's standard rates; filling gaps
          only adds what is missing and skips anything already present.
        </p>
      </div>

      {loading && rates === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : !error && rates !== null && rates.length === 0 ? (
        <EmptyState
          title="No tax rates yet"
          reason="Add a rate above, or start from a country profile — invoices and bills pick these up at posting time."
        />
      ) : rates !== null && rates.length > 0 ? (
        <>
          <div
            className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
            data-testid="tax-rates-table"
          >
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rate</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead>Applies to</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rates.map((rate) => (
                    <Fragment key={rate.id}>
                      <TableRow>
                        <TableCell className="font-medium">
                          {rate.name}
                          {rate.is_default ? (
                            <span className="ml-2 text-xs text-[var(--muted-foreground)]">
                              Default
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {rateWithBp(rate.rate_bp)}
                        </TableCell>
                        <TableCell>{humanizeLabel(rate.rate_type)}</TableCell>
                        <TableCell>{humanizeLabel(rate.kind)}</TableCell>
                        <TableCell>
                          <StatusDot
                            tone={rate.is_active ? "success" : "idle"}
                            label={rate.is_active ? "Active" : "Inactive"}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => void toggleComponents(rate)}
                            >
                              {expanded === rate.id
                                ? "Hide parts"
                                : "Split parts"}
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => openEdit(rate)}
                            >
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => setConfirmRate(rate)}
                            >
                              Remove
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {expanded === rate.id ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <SplitPartsPanel
                              rate={rate}
                              parts={components[rate.id] ?? []}
                              idPrefix="tcp"
                              compName={compName}
                              compPercent={compPercent}
                              compAccount={compAccount}
                              compWorking={compWorking}
                              onNameChange={setCompName}
                              onPercentChange={setCompPercent}
                              onAccountChange={setCompAccount}
                              onSubmit={(event) =>
                                void submitComponent(event, rate)
                              }
                              onRemove={(part) =>
                                setConfirmComponent({ rate, component: part })
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div
            className="stack-list grid gap-3 min-[64rem]:hidden"
            data-testid="tax-rates-stack"
          >
            {rates.map((rate) => (
              <article
                key={rate.id}
                className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
              >
                <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                  <span className="record-title font-medium">
                    {rate.name}
                    {rate.is_default ? (
                      <span className="ml-2 text-xs font-normal text-[var(--muted-foreground)]">
                        Default
                      </span>
                    ) : null}
                  </span>
                  <StatusDot
                    tone={rate.is_active ? "success" : "idle"}
                    label={rate.is_active ? "Active" : "Inactive"}
                  />
                </div>
                <CardField label="Rate">
                  <span className="tabular-nums">
                    {rateWithBp(rate.rate_bp)}
                  </span>
                </CardField>
                <CardField label="Applies to">
                  {humanizeLabel(rate.rate_type)}
                </CardField>
                <CardField label="Kind">{humanizeLabel(rate.kind)}</CardField>
                <CardField label="Reclaimable">
                  <span className="tabular-nums">
                    {rateWithBp(rate.recoverable_percent_bp)}
                  </span>
                </CardField>
                <div className="flex flex-wrap gap-2 pt-3">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => void toggleComponents(rate)}
                  >
                    {expanded === rate.id ? "Hide parts" : "Split parts"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => openEdit(rate)}
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirmRate(rate)}
                  >
                    Remove
                  </Button>
                </div>
                {expanded === rate.id ? (
                  <div className="mt-3 border-t border-[var(--border)] pt-3">
                    <SplitPartsPanel
                      rate={rate}
                      parts={components[rate.id] ?? []}
                      idPrefix="tcp-stack"
                      compName={compName}
                      compPercent={compPercent}
                      compAccount={compAccount}
                      compWorking={compWorking}
                      onNameChange={setCompName}
                      onPercentChange={setCompPercent}
                      onAccountChange={setCompAccount}
                      onSubmit={(event) => void submitComponent(event, rate)}
                      onRemove={(part) =>
                        setConfirmComponent({ rate, component: part })
                      }
                    />
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </>
      ) : null}

      {editing ? (
        <Drawer
          title={`Edit ${editing.name}`}
          description="Only the name and rate change here."
          open
          onOpenChange={(next) => {
            if (!next) setEditing(null);
          }}
        >
          <form onSubmit={submitEdit} className="grid gap-3">
            <label
              htmlFor="tr-edit-name"
              className="grid gap-1 text-xs font-medium"
            >
              Name
              <Input
                id="tr-edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                disabled={saving}
              />
            </label>
            <label
              htmlFor="tr-edit-rate"
              className="grid gap-1 text-xs font-medium"
            >
              Rate (%)
              <Input
                id="tr-edit-rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={editPercent}
                onChange={(e) => setEditPercent(e.target.value)}
                disabled={saving}
              />
              {toRateBp(editPercent) !== null ? (
                <span className="font-normal text-[var(--muted-foreground)]">
                  = {rateWithBp(toRateBp(editPercent) as number)}
                </span>
              ) : null}
            </label>
            <div className="flex gap-2">
              <Button type="submit" disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditing(null)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Drawer>
      ) : null}

      <Dialog
        open={confirmRate !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRate(null);
        }}
        title={confirmRate ? `Remove “${confirmRate.name}”?` : "Remove rate?"}
        description={
          confirmRate
            ? `Deletes the rate (${rateWithBp(confirmRate.rate_bp)}) and its split parts. Tax already posted to invoices and bills keeps its existing lines.`
            : undefined
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmRate(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              const rate = confirmRate;
              setConfirmRate(null);
              if (rate) void removeRate(rate);
            }}
          >
            Remove rate
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={confirmComponent !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmComponent(null);
        }}
        title={
          confirmComponent
            ? `Remove “${confirmComponent.component.name}”?`
            : "Remove split part?"
        }
        description={
          confirmComponent
            ? `Removes the part (${rateWithBp(confirmComponent.component.rate_bp)}) from “${confirmComponent.rate.name}”. The rate itself stays.`
            : undefined
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmComponent(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              const pending = confirmComponent;
              setConfirmComponent(null);
              if (pending) {
                void removeComponent(pending.rate, pending.component);
              }
            }}
          >
            Remove part
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
